import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';

export interface DashboardCountByEstado {
  estadoId: number;
  nombre: string;
  cantidad: number;
}

export interface DashboardVencimientoItem {
  extintorId: number;
  titulo: string;
  interno?: number | null;
  orden?: number | null;
  sucursalNombre?: string;
  clienteNombre?: string;
  tipo: string;
  fecha: string;
  dias: number;
}

export interface DashboardStats {
  totalClients: number;
  totalSucursales: number;
  totalExtintores: number;
  totalElementos: number;
  totalPuestos: number;
  activeExtintores: number;
  inactiveExtintores: number;
  extintoresVencidosCarga: number;
  extintoresVencidosPh: number;
  extintoresPorVencer30: number;
  extintoresEnReserva: number;
  puestosSinExtintor: number;
  pendingTasks: number;
  ordersInProcess: number;
  remitosEnCurso: number;
  remitosPorEstado: DashboardCountByEstado[];
  proximosVencer: DashboardVencimientoItem[];
  totalUsers?: number;
  totalCenters?: number;
}

const emptyStats = (): DashboardStats => ({
  totalClients: 0,
  totalSucursales: 0,
  totalExtintores: 0,
  totalElementos: 0,
  totalPuestos: 0,
  activeExtintores: 0,
  inactiveExtintores: 0,
  extintoresVencidosCarga: 0,
  extintoresVencidosPh: 0,
  extintoresPorVencer30: 0,
  extintoresEnReserva: 0,
  puestosSinExtintor: 0,
  pendingTasks: 0,
  ordersInProcess: 0,
  remitosEnCurso: 0,
  remitosPorEstado: [],
  proximosVencer: [],
});

function normalizeSummary(raw: any): DashboardStats {
  return {
    totalClients: raw.totalClients ?? raw.TotalClients ?? 0,
    totalSucursales: raw.totalSucursales ?? raw.TotalSucursales ?? 0,
    totalExtintores: raw.totalExtintores ?? raw.TotalExtintores ?? 0,
    totalElementos: raw.totalElementos ?? raw.TotalElementos ?? 0,
    totalPuestos: raw.totalPuestos ?? raw.TotalPuestos ?? 0,
    activeExtintores: raw.activeExtintores ?? raw.ActiveExtintores ?? 0,
    inactiveExtintores: raw.inactiveExtintores ?? raw.InactiveExtintores ?? 0,
    extintoresVencidosCarga: raw.extintoresVencidosCarga ?? raw.ExtintoresVencidosCarga ?? 0,
    extintoresVencidosPh: raw.extintoresVencidosPh ?? raw.ExtintoresVencidosPh ?? 0,
    extintoresPorVencer30: raw.extintoresPorVencer30 ?? raw.ExtintoresPorVencer30 ?? 0,
    extintoresEnReserva: raw.extintoresEnReserva ?? raw.ExtintoresEnReserva ?? 0,
    puestosSinExtintor: raw.puestosSinExtintor ?? raw.PuestosSinExtintor ?? 0,
    pendingTasks: raw.pendingTasks ?? raw.PendingTasks ?? 0,
    ordersInProcess: raw.ordersInProcess ?? raw.OrdersInProcess ?? 0,
    remitosEnCurso: raw.remitosEnCurso ?? raw.RemitosEnCurso ?? 0,
    remitosPorEstado: (raw.remitosPorEstado ?? raw.RemitosPorEstado ?? []).map((r: any) => ({
      estadoId: r.estadoId ?? r.EstadoId ?? 0,
      nombre: r.nombre ?? r.Nombre ?? '',
      cantidad: r.cantidad ?? r.Cantidad ?? 0,
    })),
    proximosVencer: (raw.proximosVencer ?? raw.ProximosVencer ?? []).map((v: any) => ({
      extintorId: v.extintorId ?? v.ExtintorId,
      titulo: v.titulo ?? v.Titulo ?? '',
      interno: v.interno ?? v.Interno ?? null,
      orden: v.orden ?? v.Orden ?? null,
      sucursalNombre: v.sucursalNombre ?? v.SucursalNombre,
      clienteNombre: v.clienteNombre ?? v.ClienteNombre,
      tipo: v.tipo ?? v.Tipo ?? '',
      fecha: v.fecha ?? v.Fecha,
      dias: v.dias ?? v.Dias ?? 0,
    })),
    totalUsers: raw.totalUsers ?? raw.TotalUsers ?? 0,
    totalCenters: raw.totalCenters ?? raw.TotalCenters ?? 0,
  };
}

export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    const summary = await apiRequest(createUrlWithCenterId('/api/Dashboard/summary'));
    return normalizeSummary(summary);
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return emptyStats();
  }
}
